import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    FaChartLine, 
    FaBell, 
    FaUserShield, 
    FaChartBar,
    FaCow,
    FaArrowRight
} from 'react-icons/fa6';
import logo from '../../screenshots/Milkfarm.jpeg';
import {FaCheckCircle, FaHeartbeat ,FaMobileAlt } from "react-icons/fa";
import { FaWhatsapp } from "react-icons/fa";
import { GiCow, GiBabyBottle, GiMilkCarton } from 'react-icons/gi';
import { MdPregnantWoman, MdDashboard } from 'react-icons/md';
import { PiCowFill } from "react-icons/pi";
import Accueil from '../../screenshots/accueil.png'
import AddAnimal from '../../screenshots/addAnimal.png'
import Animals from '../../screenshots/animalsList.png'
import Croissance from '../../screenshots/croissance.png'
import Reproduction from '../../screenshots/reproductionWheel.png'
import Reproduction2 from '../../screenshots/reproductionWheel2.png'
import Todo from '../../screenshots/TodoList.png'
import Users from '../../screenshots/users.png'
import ModuleRepro from '../../screenshots/ModuleReproduction.png'
import './LandingPage.css';
import { useTranslation } from 'react-i18next';
import Header from '../Headers/header';

export default function LandingPage() {
    const navigate = useNavigate();
    const [selectedImage, setSelectedImage] = useState(null);
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir(i18n.language) === 'rtl';
    const stepArrow = isRTL ? '←' : '→';
    const whatsappUrl = `https://wa.me/212718977798?text=${encodeURIComponent(
        t("home.whatsappMessage", {
            defaultValue: "مرحباً \nهل يمكنك الرد عندما تكون متاحاً؟  \nشكراً جزيلاً! ."
        })
    )}`;
    const statsSectionRef = useRef(null);
    const [hasAnimated, setHasAnimated] = useState(false);

    const features = [
        {
            icon: <GiCow />,
            title: t("home.featuresList.0.title"),
            description: t("home.featuresList.0.description"),
        },
        {
            icon: <PiCowFill />,
            title: t("home.featuresList.1.title"),
            description: t("home.featuresList.1.description"),
        },
        {
            icon: <FaBell />,
            title: t("home.featuresList.2.title"),
            description: t("home.featuresList.2.description"),
        },
        {
            icon: <FaChartBar />,
            title: t("home.featuresList.3.title"),
            description: t("home.featuresList.3.description"),
        },
        {
            icon: <FaMobileAlt />,
            title: t("home.featuresList.4.title"),
            description: t("home.featuresList.4.description"),
        },
        {
            icon: <FaUserShield />,
            title: t("home.featuresList.5.title"),
            description: t("home.featuresList.5.description"),
        }
    ];

    const howItWorks = [
        {
            step: "01",
            title: t("home.howItWorksSteps.0.title"),
            description: t("home.howItWorksSteps.0.description"),
        },
        {
            step: "02",
            title: t("home.howItWorksSteps.1.title"),
            description: t("home.howItWorksSteps.1.description"),
        },
        {
            step: "03",
            title: t("home.howItWorksSteps.2.title"),
            description: t("home.howItWorksSteps.2.description"),
        }
    ];

    const benefits = [
        {
            icon: <FaCheckCircle />,
            text: t("home.benefitsList.0.text")
        },
        {
            icon: <FaCheckCircle />,
            text: t("home.benefitsList.1.text")
        },
        {
            icon: <FaCheckCircle />,
            text: t("home.benefitsList.2.text")
        },
    ];

    const pricingPlans = [
        {
            name: t("home.pricing.plans.basic.name"),
            price: t("home.pricing.plans.basic.price"),
            description: t("home.pricing.plans.basic.description"),
            features: [
                t("home.pricing.plans.basic.features.0"),
                t("home.pricing.plans.basic.features.1"),
                t("home.pricing.plans.basic.features.2"),
            ],
            cta: t("home.pricing.plans.basic.cta"),
        },
        {
            name: t("home.pricing.plans.pro.name"),
            price: t("home.pricing.plans.pro.price"),
            description: t("home.pricing.plans.pro.description"),
            features: [
                t("home.pricing.plans.pro.features.0"),
                t("home.pricing.plans.pro.features.1"),
                t("home.pricing.plans.pro.features.2"),
            ],
            cta: t("home.pricing.plans.pro.cta"),
            badge: t("home.pricing.badges.recommended"),
            highlighted: true,
        },
        {
            name: t("home.pricing.plans.premium.name"),
            price: t("home.pricing.plans.premium.price"),
            description: t("home.pricing.plans.premium.description"),
            features: [
                t("home.pricing.plans.premium.features.0"),
                t("home.pricing.plans.premium.features.1"),
                t("home.pricing.plans.premium.features.2"),
                t("home.pricing.plans.premium.features.3"),
            ],
            cta: t("home.pricing.plans.premium.cta"),
            badge: t("home.pricing.badges.flexible"),
        },
        {
            name: t("home.pricing.plans.custom.name"),
            price: t("home.pricing.plans.custom.price"),
            description: t("home.pricing.plans.custom.description"),
            features: [
                t("home.pricing.plans.custom.features.0"),
                t("home.pricing.plans.custom.features.1"),
                t("home.pricing.plans.custom.features.2"),
                t("home.pricing.plans.custom.features.3"),
            ],
            cta: t("home.pricing.plans.custom.cta"),
            contact: true,
        },
    ];

    const stats = useMemo(() => ([
        { value: 500, suffix: "+", label: t("home.stats.0.label") },
        { value: 100, suffix: "+", label: t("home.stats.1.label") },
        { value: 98, suffix: "%", label: t("home.stats.2.label") },
        { value: 24, suffix: "/7", label: t("home.stats.3.label") }
    ]), [t]);

    const [animatedStats, setAnimatedStats] = useState(() => stats.map(() => 0));

    // Trigger the animation only when the stats section enters the viewport.
    useEffect(() => {
        const target = statsSectionRef.current;
        if (!target) return undefined;

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setHasAnimated(true);
                    observer.disconnect();
                }
            });
        }, { threshold: 0.35 });

        observer.observe(target);
        return () => observer.disconnect();
    }, []);

    // Run the count-up once after the section becomes visible.
    useEffect(() => {
        if (!hasAnimated) return undefined;

        setAnimatedStats(stats.map(() => 0));

        const duration = 1600; // ms
        const start = performance.now();
        let frameId;

        const tick = (now) => {
            const progress = Math.min((now - start) / duration, 1);
            setAnimatedStats(stats.map((stat) => Math.floor(progress * stat.value)));
            if (progress < 1) {
                frameId = requestAnimationFrame(tick);
            }
        };

        frameId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frameId);
    }, [hasAnimated, stats]);

    const screenshots = [
        { src: Accueil, alt: "Tableau de bord d'accueil" },
        { src: Animals, alt: "Liste des animaux" },
        { src: AddAnimal, alt: "Ajouter un animal" },
        { src: Reproduction, alt: "Roue de reproduction" },
        { src: Reproduction2, alt: "Roue de reproduction" },
        
    ];

    return (
        <div className="landing-page">
            <Header />
            {/* Hero Section */}
            <section id='home' className="hero-section">
                <div className="hero-container">
                    <div className="hero-content">
                        <div className="hero-badge">
                            <span className="badge-dot"></span>
                            {t("home.hero.badge")}
                        </div>
                        <h1 className="hero-title">
                            {t("home.hero.title1")}
                            <span className="gradient-text">{t("home.hero.title2")}</span>
                        </h1>
                        <p className="hero-description1">
                         {t("home.hero.description")}</p>
                        <div className="hero-buttons">
                            <button 
                                className="btn-primary"
                                onClick={() => navigate('/signup')}
                            >
                                {t("home.hero.startFree")}
                                <FaArrowRight />
                            </button>
                            <button 
                                className="btn-secondary"
                                onClick={() => navigate('/signin')}
                            >
                                <MdDashboard />
                                {t("home.hero.viewDemo")}
                            </button>
                        </div>
                    </div>
                    {/* <div className="hero-image">
                        <div className="floating-card card-1">
                            <GiCow className="card-icon" />
                            <div>
                                <div className="card-number">245</div>
                                <div className="card-label">{t("home.hero.animals")}</div>
                            </div>
                        </div>
                        <div className="floating-card card-2">
                            <MdPregnantWoman className="card-icon" />
                            <div>
                                <div className="card-number">89</div>
                                <div className="card-label">{t("home.hero.pregnant")}</div>
                            </div>
                        </div>
                        <div className="floating-card card-3">
                            <FaChartLine className="card-icon" />
                            <div>
                                <div className="card-number">+18%</div>
                                <div className="card-label">{t("home.hero.performance")}</div>
                            </div>
                        </div>
                    </div> */}
                </div>
                
                {/* Screenshots Carousel */}
                
            </section>

            {/* Image Modal */}
            {selectedImage && (
                <div 
                    className="image-modal-overlay"
                    onClick={() => setSelectedImage(null)}
                >
                    <div className="image-modal-content">
                        <button 
                            className="image-modal-close"
                            onClick={() => setSelectedImage(null)}
                        >
                            ✕
                        </button>
                        <img 
                            src={selectedImage.src} 
                            alt={selectedImage.alt}
                            className="image-modal-img"
                        />
                        <p className="image-modal-caption">{selectedImage.alt}</p>
                    </div>
                </div>
            )}

            {/* ── Stats / Reproduction Section ── */}
            <section className="stats-section1" ref={statsSectionRef}>

            {/* Top: Text + Image */}
            <div className="stats-repro">

                {/* Left: Text */}
                <div className="text-repro">
                <h1>{t("home.reproduction.title")}</h1>
                <p>{t("home.reproduction.description")}</p>
                </div>

                {/* Right: Image with decorative frame */}
                <div className="stats-repro-image-wrap">
                <div className="stats-repro-image-inner">
                    <img
                    src={screenshots[4].src}
                    alt={screenshots[4].alt}
                    onClick={() => setSelectedImage(screenshots[4])}
                    />
                    {/* Floating badge */}
                    <div className="stats-repro-badge">
                    <span className="stats-repro-badge-dot" />
                    {t("home.reproduction.title")}
                    </div>
                </div>
                </div>

            </div>

            {/* Bottom: Stats */}
            <div className="container1">
                <div className="stats-grid1">
                {stats.map((stat, index) => {
                    const displayValue = `${animatedStats[index] ?? 0}${stat.suffix || ''}`;
                    return (
                        <div key={index} className="stat-item1">
                            <div className="stat-number1">{displayValue}</div>
                            <div className="stat-label1">{stat.label}</div>
                        </div>
                    );
                })}
                </div>
            </div>

        </section>

            {/* Features Section */}
            <section id="features" className="features-section1">
                <div className="container1">
                    <div className="section-header1">
                        <h2 className="section-title1">{t("home.sections.features.title")}</h2>
                        <p className="section-subtitle1">
                            {t("home.sections.features.subtitle")}
                        </p>
                    </div>
                    <div className="features-grid1">
                        {features.map((feature, index) => (
                            <div key={index} className="feature-card1">
                                <div className="feature-icon1">{feature.icon}</div>
                                <h3 className="feature-title1">{feature.title}</h3>
                                <p className="feature-description1">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section id="how-it-works" className="how-it-works-section">
                <div className="container1">
                    <div className="section-header1">
                        <h2 className="section-title1">{t("home.sections.howItWorks.title")}</h2>
                        <p className="section-subtitle1">
                            {t("home.sections.howItWorks.subtitle")}
                        </p>
                    </div>
                    <div className="steps-grid1">
                        {howItWorks.map((step, index) => (
                            <div key={index} className="step-card1">
                                <div className="step-number1">{step.step}</div>
                                <h3 className="step-title1">{step.title}</h3>
                                <p className="step-description1">{step.description}</p>
                                {index < howItWorks.length - 1 && (
                                    <div className="step-arrow">{stepArrow}</div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section id="benefits" className="benefits-section1">
                <div className="container1">
                    <div className="benefits-content1">
                        <div className="benefits-left1">
                            <h2 className="section-title1">{t("home.sections.benefits.title")}</h2>
                            <p className="section-subtitle1">
                                {t("home.sections.benefits.subtitle")}
                            </p>
                            <div className="benefits-list1">
                                {benefits.map((benefit, index) => (
                                    <div key={index} className="benefit-item1">
                                        <div className="benefit-icon1">{benefit.icon}</div>
                                        <span>{benefit.text}</span>
                                    </div>
                                ))}
                            </div>
                            <button 
                                className="btn-primary"
                                onClick={() => navigate('/signup')}
                            >
                                {t("home.cta.startNow")}
                                <FaArrowRight />
                            </button>
                        </div>
                        <div className="benefits-right">
                            <div className="benefit-visual">
                                <div className="visual-item">
                                    <GiBabyBottle className="visual-icon" />
                                    <span>{t("home.benefitsVisual.calving")}</span>
                                </div>
                                <div className="visual-item">
                                    <FaHeartbeat className="visual-icon" />
                                    <span>{t("home.benefitsVisual.health")}</span>
                                </div>
                                <div className="visual-item">
                                    <GiMilkCarton className="visual-icon" />
                                    <span>{t("home.benefitsVisual.milk")}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section id="pricing" className="pricing-section1">
                <div className="container1">
                    <div className="section-header1">
                        <h2 className="section-title1">{t("home.sections.pricing.title")}</h2>
                        <p className="section-subtitle1">
                            {t("home.sections.pricing.subtitle")}
                        </p>
                    </div>

                    <div className="pricing-grid1">
                        {pricingPlans.map((plan, index) => (
                            <article
                                key={index}
                                className={`pricing-card1${plan.highlighted ? ' pricing-card-featured1' : ''}${plan.contact ? ' pricing-card-contact1' : ''}`}
                            >
                                {plan.badge && (
                                    <div className={`pricing-badge1${plan.highlighted ? ' pricing-badge-featured1' : ''}`}>
                                        {plan.badge}
                                    </div>
                                )}

                                <div className="pricing-card-head1">
                                    <h3 className="pricing-plan-name1">{plan.name}</h3>
                                    <div className="pricing-plan-price1">{plan.price}</div>
                                    <p className="pricing-plan-description1">{plan.description}</p>
                                </div>

                                <div className="pricing-plan-features1">
                                    {plan.features.map((feature, featureIndex) => (
                                        <div key={featureIndex} className="pricing-feature-item1">
                                            <FaCheckCircle className="pricing-feature-icon1" />
                                            <span>{feature}</span>
                                        </div>
                                    ))}
                                </div>

                                {plan.contact ? (
                                    <a
                                        href={whatsappUrl}
                                        className="pricing-btn1 pricing-btn-secondary1"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {plan.cta}
                                        <FaArrowRight />
                                    </a>
                                ) : (
                                    <button
                                        className={`pricing-btn1${plan.highlighted ? ' pricing-btn-featured1' : ''}`}
                                        onClick={() => navigate('/signup')}
                                    >
                                        {plan.cta}
                                        <FaArrowRight />
                                    </button>
                                )}
                            </article>
                        ))}
                    </div>

                    <div className="pricing-footnote1">
                        <span>{t("home.pricing.notes.0")}</span>
                        <span>{t("home.pricing.notes.1")}</span>
                        <span>{t("home.pricing.notes.2")}</span>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section">
                <div className="container1">
                    <div className="cta-content">
                        <h2>{t("home.cta.title")}</h2>
                        <p>{t("home.cta.subtitle")}</p>
                        <button 
                            className="btn-cta"
                            onClick={() => navigate('/signup')}
                        >
                            {t("home.cta.startFree")}
                            <FaArrowRight />
                        </button>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="landing-footer">
                <div className="container1">
                    <div className="footer-content1">
                        <div className="footer-section1">
                            <div className="footer-logo1">
                                <img src={logo} alt="Milk Farm Logo" className="footer-logo-img1" />
                                <span>{t("home.footer.brandName")}</span>
                            </div>
                            <p>{t("home.footer.description")}</p>
                        </div>
                        <div className="footer-section1">
                            <h4>{t("home.footer.product")}</h4>
                            <a href="#features">{t("home.footer.features")}</a>
                            <a href="#how-it-works">{t("home.footer.howItWorks")}</a>
                            <a href="#benefits">{t("home.footer.benefits")}</a>
                            <a href="#pricing">{t("home.footer.pricing")}</a>
                        </div>
                        <div className="footer-section">
                            <h4>{t("home.footer.support")}</h4>
                            <a href="#">{t("home.footer.documentation")}</a>
                            <a href="mailto:contact@ghaythapp.com">{t("home.footer.contact")}</a>
                            <a href="#">{t("home.footer.faq")}</a>
                        </div>
                        <div className="footer-section1">
                            <h4>{t("home.footer.legal")}</h4>
                            <a href="/privacy/">{t("home.footer.privacy")}</a>
                            <a href="/terms/">{t("home.footer.terms")}</a>
                            <a href="/data-deletion/">{t("home.footer.dataDeletion")}</a>
                        </div>
                    </div>
                    <div className="footer-bottom1">
                        <p>{t("home.footer.rights")}</p>
                    </div>
                </div>
            </footer>

            <a
                href={whatsappUrl}
                className="whatsapp-float"
                target="_blank"
                rel="noreferrer"
                aria-label={t("home.whatsappCta",)}
                title={t("home.whatsappCta",)}
            >
                <FaWhatsapp />
            </a>
        </div>
    );
}